import React from 'react';
import { create } from "zustand";

import type { AppDefinition, StoredAppDefinition } from '../types/apps';
import { createFileLogger } from '../utils/logger';

// ✅ FIX: Lazy load ALL apps to prevent circular dependencies
// The circular dependency was: appRegistryStore → SettingsApp → AccountTab → useAppRegistryStore
// By lazy loading all apps, we break this cycle and let webpack handle module initialization properly
const SettingsApp = React.lazy(() => import('../apps/SettingsApp/SettingsApp'));
const WebDocumentApp = React.lazy(() => import('../apps/WebDocumentApp/WebDocumentApp'));
const BrowserApp = React.lazy(() => import('../apps/BrowserApp/BrowserApp'));
const ChatApp = React.lazy(() => import('../apps/ChatApp/ChatApp'));
const RiverDriveApp = React.lazy(() => import('../apps/RiverDriveApp/RiverDriveApp'));
const MediaViewerApp = React.lazy(() => import('../apps/MediaViewerApp/MediaViewerApp').then(m => ({ default: m.MediaViewerApp })));
const PDFiumApp = React.lazy(() => import('../apps/PDFiumApp/PDFiumApp').then(m => ({ default: m.PDFiumApp })));
const DocsApp = React.lazy(() => import('../apps/DocsApp/components/DocsApp'));
const DocsApp_v2 = React.lazy(() => import('../apps/DocsApp_v2/DocsApp_v2')); // ✅ NEW: v2 Documents app
const CanvasApp_v2 = React.lazy(() => import('../apps/CanvasApp_v2/CanvasApp_v2')); // ✅ NEW: v2 Canvas app
const SheetsApp_v2 = React.lazy(() => import('../apps/SheetsApp_v2/SheetsApp_v2')); // ✅ NEW: v2 Sheets app
const PresentationsApp_v2 = React.lazy(() => import('../apps/PresentationsApp_v2/PresentationsApp_v2')); // ✅ NEW: v2 Presentations app
const NotesApp_v2 = React.lazy(() => import('../apps/NotesApp_v2/NotesApp_v2')); // ✅ NEW: v2 Notes app
const PageApp_v2 = React.lazy(() => import('../apps/PageApp_v2/PageApp_v2')); // ✅ NEW: v2 Page app
const AppBuilderApp = React.lazy(() => import('../apps/AppBuilderApp/AppBuilderApp'));
const FileViewerApp = React.lazy(() => import('../apps/FileViewerApp/FileViewerApp'));
const FileEditorApp = React.lazy(() => import('../apps/FileEditorApp/FileEditorApp'));
const IngestWizard = React.lazy(() => import('../apps/IngestWizard/IngestWizard'));
const StoreApp = React.lazy(() => import('../apps/StoreApp/StoreApp'));
const LLMChef = React.lazy(() => import('../apps/LLMChef').then(m => ({ default: m.LLMChef })));
const TasksApp = React.lazy(() => import('../apps/TasksApp/TasksApp'));
const LegalCodeViewerApp = React.lazy(() => import('../apps/LegalCodeViewerApp/LegalCodeViewerApp'));
const EntityViewApp = React.lazy(() => import('../apps/EntityViewApp/EntityViewApp'));
const NotesMarkdownApp = React.lazy(() => import('../apps/NotesMarkdownApp/NotesMarkdownApp').then(m => ({ default: m.NotesMarkdownApp })));
const NeurvanaApp = React.lazy(() => import('../apps/NeurvanaApp/NeurvanaApp'));
const PromptLibraryApp = React.lazy(() => import('../apps/PromptLibraryApp/PromptLibraryApp'));
const StoryCharacterApp = React.lazy(() => import('../apps/StoryCharacterApp/StoryCharacterApp'));
const MediaGenApp = React.lazy(() => import('../apps/MediaGenApp/MediaGenApp'));
const GoogleDriveFileApp = React.lazy(() => import('../apps/GoogleDriveFileApp/GoogleDriveFileApp'));
const GoogleDriveApp = React.lazy(() => import('../apps/GoogleDriveApp/GoogleDriveApp'));
const LocalFileApp = React.lazy(() => import('../apps/LocalFileApp/LocalFileApp'));
const LibraryApp = React.lazy(() => import('../apps/LibraryApp/LibraryApp').then(m => ({ default: m.LibraryApp })));
const CanvasApp = React.lazy(() => import('../apps/CanvasApp/CanvasApp').then(m => ({ default: m.CanvasApp })));
const APIConnectionApp = React.lazy(() => import('../apps/APIConnectionApp/APIConnectionApp').then(m => ({ default: m.APIConnectionApp })));
const IntegrationsApp = React.lazy(() => import('../apps/IntegrationsApp/IntegrationsApp').then(m => ({ default: m.IntegrationsApp })));
const BankApp = React.lazy(() => import('../apps/BankApp/components/BankApp'));
const QuickLauncherApp = React.lazy(() => import('../apps/QuickLauncherApp/QuickLauncherApp'));
const MailApp = React.lazy(() => import('../apps/MailApp/MailApp').then(m => ({ default: m.MailApp })));
const WritersDashboardApp_v2 = React.lazy(() => import('../apps/WritersDashboardApp_v2/WritersDashboardApp_v2'));
const BookFormatterApp_v2 = React.lazy(() => import('../apps/BookFormatterApp_v2/BookFormatterApp_v2'));
const ManuscriptReviewReportApp_v2 = React.lazy(() => import('../apps/ManuscriptReviewReportApp_v2/ManuscriptReviewReportApp_v2'));
const PasswordVaultApp = React.lazy(() => import('../apps/PasswordVaultApp/PasswordVaultApp').then(m => ({ default: m.PasswordVaultApp })));
const ContactsApp = React.lazy(() => import('../apps/ContactsApp/ContactsApp').then(m => ({ default: m.ContactsApp })));
const AutomationsApp = React.lazy(() => import('../apps/AutomationsApp/AutomationsApp'));
const ListenApp = React.lazy(() => import('../apps/ListenApp/ListenApp'));
const BookkeeperApp = React.lazy(() => import('../apps/BookkeeperApp/BookkeeperApp'));
const RulesApp_v2 = React.lazy(() => import('../apps/RulesApp_v2/RulesApp_v2'));
const TerminalApp = React.lazy(() => import('../apps/TerminalApp/TerminalApp'));

// 🎯 PERFORMANCE: Preload functions for common apps (call early to avoid Suspense flash)
export const preloadQuickLauncher = () => {
  import('../apps/QuickLauncherApp/QuickLauncherApp').catch(() => {
    // Ignore preload errors - lazy load will retry when actually needed
  });
};

export const preloadCommonApps = () => {
  // Preload most commonly used apps on initial page load
  import('../apps/SettingsApp/SettingsApp').catch(() => {});
  import('../apps/DocsApp/components/DocsApp').catch(() => {});
  import('../apps/ChatApp/ChatApp').catch(() => {});
};

// ✅ FIX: All apps are now lazy-loaded React components (consistent with QuickLauncherApp pattern)
// This breaks circular dependencies and prevents "Cannot access 'X' before initialization" errors
const componentMap: Record<string, React.ComponentType<any>> = {
  pdfium: PDFiumApp,
  entityBrowser: RiverDriveApp,
  entityBrowser2: RiverDriveApp,
  webdocument: WebDocumentApp,
  settings: SettingsApp,
  browser: BrowserApp,
  chat: ChatApp,
  mediaviewer: MediaViewerApp,
  docs: DocsApp,
  'docs-v2': DocsApp_v2, // ✅ NEW: v2 Documents app
  'canvas-v2': CanvasApp_v2, // ✅ NEW: v2 Canvas app
  'sheets-v2': SheetsApp_v2, // ✅ NEW: v2 Sheets app
  'presentations-v2': PresentationsApp_v2, // ✅ NEW: v2 Presentations app
  'notes-v2': NotesApp_v2, // ✅ NEW: v2 Notes app
  'page-v2': PageApp_v2, // ✅ NEW: v2 Page app
  'file-viewer': FileEditorApp, // backward-compat alias → FileEditorApp
  'file-editor': FileEditorApp,
  'app-builder': AppBuilderApp, // ✅ NEW: App Builder IDE
  bank: BankApp,
  ingestWizard: IngestWizard,
  store: StoreApp,
  llmchef: LLMChef,
  tasks: TasksApp,
  quicklauncher: QuickLauncherApp,
  'legal-code-viewer': LegalCodeViewerApp,
  'entity-view': EntityViewApp,
  'notes-markdown': NotesMarkdownApp,
  neurvana: NeurvanaApp,
  'prompt-library': PromptLibraryApp,
  'story-character': StoryCharacterApp,
  mediagen: MediaGenApp,
  'media-gen': MediaGenApp, // Legacy alias for persisted windows
  'google-drive-file': GoogleDriveFileApp,
  'drive': GoogleDriveApp,
  'local-file': LocalFileApp,
  library: LibraryApp,
  'api-connections': IntegrationsApp, // now handled by IntegrationsApp (backward compat)
  integrations: IntegrationsApp,
  canvas: CanvasApp,
  mail: MailApp,
  'mail-app': MailApp, // alias used by entityTypeMapping for Mail.Thread / Mail.Draft routing
  writers_dashboard: WritersDashboardApp_v2,
  book_formatter_v2: BookFormatterApp_v2,
  'manuscript-review-report': ManuscriptReviewReportApp_v2,
  'password-vault': PasswordVaultApp,
  contacts: ContactsApp,
  automations: AutomationsApp,
  goals: AutomationsApp, // legacy alias — "GoalsApp" windows rendered by AutomationsApp
  agent: AutomationsApp, // legacy alias — "AgentApp" windows rendered by AutomationsApp
  'listen-app': ListenApp,
  bookkeeper: BookkeeperApp,
  rules: RulesApp_v2,
  terminal: TerminalApp,
};

interface AppRegistryState {
  apps: Record<string, StoredAppDefinition>;
  componentMap: Record<string, React.ComponentType<any>>;
  appsInitialized: boolean;
  initializeApps: () => Promise<void>;
}

export const useAppRegistryStore = create<AppRegistryState>((set, get) => ({
  apps: {},
  componentMap,
  appsInitialized: false,
  initializeApps: async () => {
    const log = createFileLogger('APP_REGISTRY');
    if (get().appsInitialized) {
      log.info('[APP_REGISTRY] Already initialized, skipping');
      return;
    }
    try {
      // Use dynamic import to avoid circular dependency
      const { appRegistry } = await import('../services/appRegistryService');
      const installedApps = await appRegistry.getInstalledApps();

      const formattedApps = installedApps.reduce<Record<string, StoredAppDefinition>>((acc, app: AppDefinition) => {
        acc[app.appId] = {
          _id: '', // Required by StoredAppDefinition
          entityType: 'App',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          skeleton: app,
        };
        return acc;
      }, {});

      set({ apps: formattedApps, appsInitialized: true });
    } catch (error) {
      log.error('Failed to initialize apps:', error);
    }
  },
}));
